import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Tab, Nav, Pagination } from "react-bootstrap";
import Select from "react-select";
import {
  getAllCoins,
  getCoinGeneralSettings,
  getFavoriteCoinIds,
  updateFavoriteCoin,
} from "../../../api/coin";
import { usePrices } from "../../../context/SocketContext";

export const COIN_CATEGORIES = {
  0: "All",
  1: "Sport",
  2: "E-Sport",
  3: "MEME",
  4: "AI",
  5: "Exchange Based",
  6: "Stable",
  7: "Digital Assets",
  8: "Favorite",
};

const getCategoryId = (categoryName) => {
  return Object.entries(COIN_CATEGORIES).find(
    ([_, val]) => val === categoryName
  )?.[0];
};

const Market = () => {
  const prices = usePrices();
  // console.log("PRICE", prices)
  const [coinData, setCoinData] = useState([]);
  const [coinPrices, setCoinPrices] = useState({});
  const [coinColors, setCoinColors] = useState({});
  const [coin24HPer, setCoin24HPer] = useState({});
  const [topGainers, setTopGainers] = useState([]);
  const [topLosers, setTopLosers] = useState([]);
  const [highlights, setHighlights] = useState([]);
  const [listings, setListings] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const listRequestRef = useRef(0);

  const [activeCategory, setActiveCategory] = useState("All");
  const [activePage, setActivePage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 574);

  const recordsPerPage = 6;
  const navigate = useNavigate();

  useEffect(() => {
    const updateMap = (prev, valueKey) => {
      const updated = { ...prev };
      prices.forEach((item) => {
        updated[item.coinId] = item[valueKey] ?? 0;
      });
      return updated;
    };

    setCoinPrices((prev) => updateMap(prev, "price"));
    setCoin24HPer((prev) => updateMap(prev, "percentChange"));
    setCoinColors((prev) => {
      const updated = { ...prev };
      prices.forEach(({ coinId, color }) => {
        updated[coinId] = color ?? "#000000";
      });
      return updated;
    });
  }, [prices]);
  const [sortOrder, setSortOrder] = useState("latest");

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    const controller = new AbortController();
    const requestId = ++listRequestRef.current;
    const fetchCoins = async () => {
      try {
        const coinTypeId =
          activeCategory === "All"
            ? null
            : Number(getCategoryId(activeCategory));
        const response = await getAllCoins(
          activePage,
          recordsPerPage,
          debouncedSearch,
          coinTypeId,
          sortOrder,
          controller.signal
        );

        if (requestId !== listRequestRef.current || controller.signal.aborted) {
          return;
        }

        const coinsWithCategory = response.data.map((coin) => ({
          ...coin,
          category: COIN_CATEGORIES[coin.coinType] || null,
        }));

        setCoinData(coinsWithCategory);
        setTotalCount(response.total);
      } catch (error) {
        if (error?.name === "CanceledError" || error?.name === "AbortError") {
          return;
        }
        console.error("Error fetching coins:", error);
      }
    };
    fetchCoins();
    return () => controller.abort();
  }, [activePage, activeCategory, debouncedSearch, sortOrder]);

  useEffect(() => {
    setActivePage(1); // Reset to page 1 when category changes
  }, [activeCategory]);

  const totalPages = Math.ceil(totalCount / recordsPerPage);

  const handlePageChange = useCallback((page) => {
    setActivePage(page);
  }, []);

  const renderPagination = () => {
    const items = [];
    const visiblePages = 5;

    let startPage = Math.max(1, activePage - Math.floor(visiblePages / 2));
    let endPage = startPage + visiblePages - 1;

    if (endPage > totalPages) {
      endPage = totalPages;
      startPage = Math.max(1, endPage - visiblePages + 1);
    }

    if (startPage > 1) {
      items.push(
        <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
          1
        </Pagination.Item>
      );
      if (startPage > 2) {
        items.push(
          <span key="start-ellipsis" className="px-2 text-primary">
            ...
          </span>
        );
      }
    }

    for (let number = startPage; number <= endPage; number++) {
      items.push(
        <Pagination.Item
          key={number}
          active={number === activePage}
          onClick={() => handlePageChange(number)}
        >
          {number}
        </Pagination.Item>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        items.push(
          <span key="end-ellipsis" className="px-2 text-primary">
            ...
          </span>
        );
      }
      items.push(
        <Pagination.Item
          key={totalPages}
          onClick={() => handlePageChange(totalPages)}
        >
          {totalPages}
        </Pagination.Item>
      );
    }

    return (
      <Pagination className="mt-4 pagination-gutter pagination-primary no-bg">
        <li className="page-item page-indicator">
          <Link
            className="page-link"
            to="#"
            onClick={() => activePage > 1 && handlePageChange(activePage - 1)}
          >
            <i className="la la-angle-left" />
          </Link>
        </li>
        {items}
        <li className="page-item page-indicator">
          <Link
            className="page-link"
            to="#"
            onClick={() =>
              activePage < totalPages && handlePageChange(activePage + 1)
            }
          >
            <i className="la la-angle-right" />
          </Link>
        </li>
      </Pagination>
    );
  };

  const fetchAdminSelectedCoins = async (signal) => {
    try {
      const response = await getCoinGeneralSettings(signal);
      console.log("RES", response);
      setTopGainers(response.topGainers || []);
      setTopLosers(response.topLosers || []);
      setHighlights(response.topHighLights || []);
      setListings(response.topListings || []);
      const updatedFavorites = await getFavoriteCoinIds(signal); // Should return array of coin IDs
      setFavoriteCoinIds(updatedFavorites);
    } catch (error) {
      if (error?.name === "CanceledError" || error?.name === "AbortError") {
        return;
      }
      console.error("Error fetching admin selected coins:", error);
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    fetchAdminSelectedCoins(controller.signal);
    return () => controller.abort();
  }, []);

  const [favoriteCoinIds, setFavoriteCoinIds] = useState([]);
  const sortOptions = [
    { value: "latest", label: "Latest" },
    { value: "highToLow", label: "High to Low" },
    { value: "lowToHigh", label: "Low to High" },
  ];

  const toggleFavorite = async (coinId) => {
    const isFav = favoriteCoinIds.includes(coinId);
    try {
      // Add or remove favorite
      await updateFavoriteCoin({ coinId, type: isFav ? "remove" : "add" });

      // Refetch updated favorites from backend
      const updatedFavorites = await getFavoriteCoinIds(); // Should return array of coin IDs
      setFavoriteCoinIds(updatedFavorites);
    } catch (error) {
      console.error("Error updating favorite coin:", error);
    }
  };

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 574);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <div className="">
      {/* Cards */}
      <div className="col-xl-12">
        <div className="">
          <div className="row">
            {/* Top */}
            <div className="col-xl-3 col-lg-6 col-md-12 ">
              <div className="card">
                <div className="card-body py-3">
                  <h4 className="fs-18 mb-3">Top</h4>
                  <div className="overflow-auto" style={{ maxHeight: "250px" }}>
                    {topGainers.length === 0 ? (
                      <p>No gainers</p>
                    ) : (
                      topGainers.map((coin) => {
                        const price =
                          coinPrices[coin.id] ??
                          coin?.coinPriceHistory?.[0]?.price ??
                          0;
                        const percent =
                          coin24HPer[coin.id] ??
                          coin?.coinPriceHistory?.[0]?.percentChange ??
                          "0%";
                        const color =
                          coinColors[coin.id] ??
                          coin?.coinPriceHistory?.[0]?.color ??
                          "";

                        return (
                          <div
                            key={coin.id}
                            className="market-list d-flex align-items-center justify-content-between mb-2"
                          >
                            <Link
                              to="#"
                              className="market-title d-flex align-items-center"
                            >
                              <img
                                src={`${process.env.REACT_APP_API_BASE_URL}/uploads/${coin.logo}`}
                                alt={coin.symbol}
                                style={{ width: "30px", height: "30px" }}
                                className="rounded-circle"
                              />
                              <h5 className="mb-0 ms-2">{coin.symbol}</h5>
                            </Link>
                            <span className="fs-14" style={{ color }}>
                              ${Number(price).toFixed(2)}
                            </span>
                            <span className="fs-14" style={{ color }}>
                              {percent}
                            </span>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Top Losers */}
            <div className="col-xl-3 col-lg-6 col-md-12">
              <div className="card">
                <div className="card-body py-3">
                  <h4 className="fs-18 mb-3">Trending</h4>

                  <div className="overflow-auto" style={{ maxHeight: "250px" }}>
                    {topLosers.length === 0 ? (
                      <p>No losers</p>
                    ) : (
                      topLosers.map((coin) => {
                        const price =
                          coinPrices[coin.id] ??
                          coin?.coinPriceHistory?.[0]?.price ??
                          0;
                        const percent =
                          coin24HPer[coin.id] ??
                          coin?.coinPriceHistory?.[0]?.percentChange ??
                          "0%";
                        const color =
                          coinColors[coin.id] ??
                          coin?.coinPriceHistory?.[0]?.color ??
                          "";

                        return (
                          <div
                            key={coin.id}
                            className="market-list d-flex align-items-center justify-content-between mb-2"
                          >
                            <Link
                              to="#"
                              className="market-title d-flex align-items-center"
                            >
                              <img
                                src={`${process.env.REACT_APP_API_BASE_URL}/uploads/${coin.logo}`}
                                alt={coin.symbol}
                                style={{ width: "30px", height: "30px" }}
                                className="rounded-circle"
                              />
                              <h5 className="mb-0 ms-2">{coin.symbol}</h5>
                            </Link>
                            <span className="fs-14" style={{ color }}>
                              ${Number(price).toFixed(2)}
                            </span>
                            <span className="fs-14" style={{ color }}>
                              {percent}
                            </span>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Highlights */}
            <div className="col-xl-3 col-lg-6 col-md-12">
              <div className="card">
                <div className="card-body py-3">
                  <h4 className="fs-18 mb-3">Most Visited</h4>
                  <div className="overflow-auto" style={{ maxHeight: "250px" }}>
                    {highlights.length === 0 ? (
                      <p>No highlights</p>
                    ) : (
                      highlights.map((coin) => {
                        const price =
                          coinPrices[coin.id] ??
                          coin?.coinPriceHistory?.[0]?.price ??
                          0;
                        const percent =
                          coin24HPer[coin.id] ??
                          coin?.coinPriceHistory?.[0]?.percentChange ??
                          "0%";
                        const color =
                          coinColors[coin.id] ??
                          coin?.coinPriceHistory?.[0]?.color ??
                          "";

                        return (
                          <div
                            key={coin.id}
                            className="market-list d-flex align-items-center justify-content-between mb-2"
                          >
                            <Link
                              to="#"
                              className="market-title d-flex align-items-center"
                            >
                              <img
                                src={`${process.env.REACT_APP_API_BASE_URL}/uploads/${coin.logo}`}
                                alt={coin.symbol}
                                style={{ width: "30px", height: "30px" }}
                                className="rounded-circle"
                              />
                              <h5 className="mb-0 ms-2">{coin.symbol}</h5>
                            </Link>
                            <span className="fs-14" style={{ color }}>
                              ${Number(price).toFixed(2)}
                            </span>
                            <span className="fs-14" style={{ color }}>
                              {percent}
                            </span>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Listing */}
            <div className="col-xl-3 col-lg-6 col-md-12">
              <div className="card">
                <div className="card-body py-3">
                  <h4 className="fs-18 mb-3">New</h4>
                  <div className="overflow-auto" style={{ maxHeight: "250px" }}>
                    {listings.length === 0 ? (
                      <p>No listings</p>
                    ) : (
                      listings.map((coin) => {
                        const price =
                          coinPrices[coin.id] ??
                          coin?.coinPriceHistory?.[0]?.price ??
                          0;
                        const percent =
                          coin24HPer[coin.id] ??
                          coin?.coinPriceHistory?.[0]?.percentChange ??
                          "0%";
                        const color =
                          coinColors[coin.id] ??
                          coin?.coinPriceHistory?.[0]?.color ??
                          "";

                        return (
                          <div
                            key={coin.id}
                            className="market-list d-flex align-items-center justify-content-between mb-2"
                          >
                            <Link
                              to="#"
                              className="market-title d-flex align-items-center"
                            >
                              <img
                                src={`${process.env.REACT_APP_API_BASE_URL}/uploads/${coin.logo}`}
                                alt={coin.symbol}
                                style={{ width: "30px", height: "30px" }}
                                className="rounded-circle"
                              />
                              <h5 className="mb-0 ms-2">{coin.symbol}</h5>
                            </Link>
                            <span className="fs-14" style={{ color }}>
                              ${Number(price).toFixed(2)}
                            </span>
                            <span className="fs-14" style={{ color }}>
                              {percent}
                            </span>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Coin Table */}
      <div className="col-xl-12">
        <div className="card">
          <Tab.Container
            activeKey={activeCategory}
            onSelect={(k) => setActiveCategory(k)}
          >
            <div className="card-header border-0">
              {/* <Nav as="ul" className="order nav-tabs" role="tablist">
                {[
                  "All",
                  "Favorite",
                  ...Object.values(COIN_CATEGORIES).filter(
                    (c) => c !== "All" && c !== "Favorite"
                  ),
                ].map((cat) => (
                  <Nav.Item as="li" key={cat} className="my-1">
                    <Nav.Link as="button" eventKey={cat} type="button">
                      {cat === "All" ? "All Cryptos" : `${cat} Token`}
                    </Nav.Link>
                  </Nav.Item>
                ))}
              </Nav> */}
              <Nav as="ul" className="order nav-tabs flex-wrap" role="tablist">
                {[
                  "All",
                  "Favorite",
                  ...Object.values(COIN_CATEGORIES).filter(
                    (c) => c !== "All" && c !== "Favorite"
                  ),
                ].map((cat) => (
                  <Nav.Item as="li" key={cat} className="my-1 me-2">
                    <Nav.Link
                      as="button"
                      eventKey={cat}
                      type="button"
                      className={`w-100 ${
                        isMobile ? "border  rounded px-3 py-2" : ""
                      }`}
                    >
                      {cat === "All" ? "All Cryptos" : `${cat} Token`}
                    </Nav.Link>
                  </Nav.Item>
                ))}
              </Nav>
            </div>

            <div className="card-body pt-0">
              <div className="row g-2">
                <div className="col-md-10 col-12">
                  <input
                    type="text"
                    className="form-control w-100"
                    placeholder="Search by name"
                    value={searchQuery}
                    onChangeCapture={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                <div className="col-md-2 col-12">
                  <Select
                    className="custom-react-select w-100"
                    options={sortOptions}
                    value={sortOptions.find((opt) => opt.value === sortOrder)}
                    onChange={(selected) => setSortOrder(selected.value)}
                    placeholder="Sort By"
                    isSearchable={false}
                  />
                </div>
              </div>

              <Tab.Content>
                <Tab.Pane eventKey={activeCategory}>
                  <div className="table-responsive dataTablemarket">
                    <table
                      className="table dataTable shadow-hover display"
                      style={{ minWidth: "845px" }}
                    >
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th className="text-center">Favorite</th>
                          <th className="text-center">Price</th>
                          <th className="text-center">24H Change</th>
                          <th className="text-center">24H Volume</th>
                          <th className="text-center">Market Cap</th>
                        </tr>
                      </thead>
                      <tbody>
                        {coinData.map((item, index) => {
                          const price =
                            coinPrices[item.id] ??
                            item?.coinPriceHistory?.[0]?.price ??
                            0;
                          const percent =
                            coin24HPer[item.id] ??
                            item?.coinPriceHistory?.[0]?.percentChange ??
                            "0%";
                          const volume =
                            item?.coinPrice?.[0]?.hourVolume ?? "$0";
                          const marketCap =
                            item?.coinPrice?.[0]?.marketCap ?? "$0";
                          const color =
                            coinColors[item.id] ??
                            item?.coinPriceHistory?.[0]?.color ??
                            "";

                          return (
                            <tr
                              key={index}
                              onClick={() =>
                                navigate(`/coin-details/${item.id}`)
                              }
                              style={{ cursor: "pointer" }}
                            >
                              <td className="d-flex align-items-center">
                                <img
                                  src={`${process.env.REACT_APP_API_BASE_URL}/uploads/${item.logo}`}
                                  className="rounded-circle"
                                  style={{ height: "50px", width: "50px" }}
                                  alt={item.symbol}
                                />

                                <h5 className="mb-0 ms-4">{item.symbol}</h5>
                                <span className="text-muted ms-4">
                                  {item.name}
                                </span>
                              </td>
                              <td
                                className="text-center"
                                onClick={(e) => {
                                  e.stopPropagation(); // Prevent row click
                                  toggleFavorite(item.id); // Handle add/remove fav
                                }}
                              >
                                <i
                                  className={`bi ${
                                    favoriteCoinIds.includes(item.id)
                                      ? "bi-star-fill text-warning"
                                      : "bi-star"
                                  }`}
                                  style={{
                                    fontSize: "20px",
                                    cursor: "pointer",
                                  }}
                                ></i>
                              </td>
                              <td className="text-center" style={{ color }}>
                                ${Number(price).toFixed(2)}
                              </td>
                              <td className="text-center" style={{ color }}>
                                {percent}
                              </td>
                              <td className="text-center">{volume}</td>
                              <td className="text-center">{marketCap}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </Tab.Pane>
              </Tab.Content>
              <div className="d-flex justify-content-center">
                {renderPagination()}
              </div>
            </div>
          </Tab.Container>
        </div>
      </div>
    </div>
  );
};

export default Market;
